import { useQuery } from '@tanstack/react-query';

import { permissionService } from '@/services/permissionService';
import { useAuthStore } from '@/store/authStore';

import type { PermissionAction, PermissionResource, PermissionScope } from '@/permissions/types';

/**
 * Central permission hook.
 *
 * Usage:
 *   const { can, hasRole, isAdmin } = usePermission();
 *
 *   can('device', 'read')           // true if role has read:own OR read:any
 *   can('device', 'create', 'own')  // true if role has create:own OR create:any
 *   can('device', 'delete', 'any')  // true ONLY if role has delete:any (admin-level check)
 *   can('device', 'read') && !can('device', 'read', 'any')  // has own but not any
 *
 * Scope semantics mirror accesscontrol:
 *   'any' ⊇ 'own' — a role with :any permission also satisfies an :own check.
 */
export const usePermission = () => {
  const { user } = useAuthStore();
  const role = user?.role ?? '';

  const { data: rolePermissions = {} } = useQuery({
    queryKey: ['permissions', role],
    queryFn: () => permissionService.getForRole(role),
    enabled: Boolean(role),
    // Permissions are static for now; a long staleTime avoids redundant re-fetches.
    // When the API is live, reduce this to match the desired TTL.
    staleTime: Infinity,
  });

  /**
   * Check whether the current user's role grants a given action on a resource.
   *
   * @param resource  The resource name (must match keys in permissions.json)
   * @param action    The action to check: 'create' | 'read' | 'update' | 'delete'
   * @param scope     Optional scope constraint:
   *                  - omitted / 'own' → granted if role has :own OR :any
   *                  - 'any'           → granted ONLY if role has :any
   */
  const can = (
    resource: PermissionResource | string,
    action: PermissionAction,
    scope?: PermissionScope,
  ): boolean => {
    if (!role) return false;
    const grantedScope = rolePermissions[resource]?.[action];
    if (!grantedScope) return false;

    if (scope === 'any') return grantedScope === 'any';
    // No scope or 'own': satisfied by either :own or :any
    return grantedScope === 'own' || grantedScope === 'any';
  };

  const hasRole = (...roles: string[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  return { can, hasRole, role };
};
